/**
 * Icon <uo-icon>
 *
 * Attributes:
 * id  The ID of the icon
 */

// Import global styles
// This will load the font
import './style.scss';

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { isEmpty, isDefined } from '@automator/utilities';

// Import style for web component
import styles from './web-component-style.scss';

// eslint-disable-next-line require-jsdoc
@customElement( 'uo-icon' )
export class AutomatorIcon extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	@property( { type: String } ) accessor id;

	@property( { type: String } ) accessor integration;

	@property( { type: String } ) accessor size; // xsmall (75%), small (87.5%), large (125%), xlarge (150%), 2x, 16 (16px), 24 (24px), 32 (32px), 40 (40px), 64 (64px)

	@property( { type: String } ) accessor animation; // spin

	@property( { type: Boolean, attribute: 'hide-missing' } ) accessor hideMissing = false; // Renders nothing if the icon requested icon is missing

	@property( { type: Boolean, attribute: 'show-tooltip' } ) accessor showName = false;

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<div
				class="icon"
				size="${ ifDefined( this.size ) }"
				animation="${ ifDefined( this.animation ) }"
			>
				${ ! isEmpty( this.integration ) ? this.templateIntegrationIcon : this.templateGlyph }
			</div>
		`;
	}

	/**
	 * Creates the template to render a glyph
	 *
	 * @return {TemplateResult} The template
	 */
	get templateGlyph() {
		return html`
			<div
				class="icon-glyph"
				id="${ this.id }"
				?is-custom="${ this.customGlyphs.includes( this.id ) }"
				?hide-missing="${ this.hideMissing }"
			></div>
		`;
	}

	/**
	 * Creates the template to render an integration icon
	 * The main difference is that normal icons are font glyphs, while integration icons are images
	 *
	 * @return {TemplateResult} The template
	 */
	get templateIntegrationIcon() {
		// Get the integration
		let integration;

		try {
			integration = UncannyAutomatorBackend.components.icon.integrations[ this.integration ];

			// Check if the integration exists
			if ( ! isDefined( integration ) ) {
				throw new Error( `Icon: We couldn't find the integration "${ this.integration }". Showing "exclamation-circle" glyph instead.` );
			}
		} catch ( e ) {
			if ( UncannyAutomatorBackend.debugging.enabled ) {
				// eslint-disable-next-line no-console
				console.warn( e );
			}

			// Check if we should show a default icon, or if we should not render anything
			if ( this.hideMissing ) {
				return html``;
			}

			// Show fallback
			return html`<uo-icon></uo-icon>`;
		}

		// Create integration icon
		const $integrationIcon = html`
			<div 
				class="icon-integration"
				style="background-image: url(${ integration.icon })"
			></div>
		`;

		if ( ! this.showName ) {
			return $integrationIcon;
		}

		return html`
			<uo-tooltip>
				${ integration.name }

				<span slot="target">
					${ $integrationIcon }
				</span>
			</uo-tooltip>
		`;
	}

	/**
	 * List of custom icons
	 * These are icons that can't be added to the font (for example, icons with more than one color)
	 *
	 * @return {Array} ID of custom icons
	 */
	get customGlyphs() {
		return [
			'google',
			'microsoft',
			'facebook',
			'x-twitter',
			'youtube',
		];
	}
}
